import { getEffectiveEvacZones } from 'features/evacZones/utils';
import { Flooding } from 'features/flooding/flooding.types';
import { NotificationType } from 'features/notifications/notifications.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { differenceBy } from 'es-toolkit/compat';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EvacZone,
  EvacZoneStatus,
  FormEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { EVAC_ZONE_LABEL_TRANS_KEYS } from '../../../../constants';
import {
  checkEvacFieldChanged,
  findZoneNamesInMessage,
  hasEvacInfoChanged,
} from './useGeoEventEvacuationFormFieldChanges.utils';

export type EvacTransLine = {
  id: string;
  i18nKey: string;
  values: Record<string, unknown>;
};

export type EvacTransSection = {
  id: string;
  title: string;
  lines: EvacTransLine[];
};

export type EvacZoneRemovedSection = {
  title: string;
  zones: Array<{ uid: string; displayName: string }>;
};

export const EVAC_TEXT_CHANGE_KEYS = [
  'evacuationOrders',
  'evacuationWarnings',
  'evacuationAdvisories',
  'evacuationShelterInPlace',
  'evacuationNotes',
] as const;

export type EvacTextChangeKey = (typeof EVAC_TEXT_CHANGE_KEYS)[number];

type FormValues = {
  notificationType: NotificationType;
  evacZoneStatuses: FormEvacZoneStatus[];
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
  customShelterInPlace: boolean;
  evacuationOrders: string;
  evacuationWarnings: string;
  evacuationNotes: string;
  evacuationAdvisories: string;
  evacuationShelterInPlace: string;
  releasePendingZoneUids?: string[];
};

const statusIndex: Record<EvacZoneStatus, number> = {
  orders: 0,
  warnings: 1,
  advisories: 2,
  shelter_in_place: 3,
};

export type UseGeoEventEvacuationFormFieldChangesProps = {
  geoEvent: Wildfire | Flooding;
  evacZones: EvacZone[];
  zoneStyle: RegionEvacZoneStyle;
  isProvisional?: boolean;
};

export type UseGeoEventEvacuationFormFieldChangesResult = {
  evacTextChanges: Partial<Record<EvacTextChangeKey, string>>;
  evacuationInfo: { show: boolean; appendSilentLine: boolean };
  zoneMismatchLines: EvacTransLine[];
  zoneAddedSection: EvacTransSection | null;
  zoneUpgradedSection: EvacTransSection | null;
  zoneDowngradedSection: EvacTransSection | null;
  zoneRemovedSection: EvacZoneRemovedSection | null;
  hasChanges: boolean;
  showConfirmSelectedZones: boolean;
};

export const useGeoEventEvacuationFormFieldChanges = (
  props: UseGeoEventEvacuationFormFieldChangesProps,
): UseGeoEventEvacuationFormFieldChangesResult => {
  const { geoEvent, evacZones, zoneStyle, isProvisional } = props;
  const { t } = useTranslation();

  const [
    notificationType,
    evacZoneStatuses,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
    evacuationOrders,
    evacuationWarnings,
    evacuationNotes,
    evacuationAdvisories,
    evacuationShelterInPlace,
    releasePendingZoneUids,
  ] = useWatch<FormValues>({
    name: [
      'notificationType',
      'evacZoneStatuses',
      'customOrders',
      'customWarnings',
      'customAdvisories',
      'customShelterInPlace',
      'evacuationOrders',
      'evacuationWarnings',
      'evacuationNotes',
      'evacuationAdvisories',
      'evacuationShelterInPlace',
      'releasePendingZoneUids',
    ],
  }) as [
    NotificationType,
    FormEvacZoneStatus[],
    boolean,
    boolean,
    boolean,
    boolean,
    string,
    string,
    string,
    string,
    string,
    string[] | undefined,
  ];

  const hasEvacZones = evacZones.length > 0;

  const evacuationNotesChanged = hasEvacInfoChanged(
    geoEvent.data.evacuationNotes,
    evacuationNotes,
  );

  const evacuationWarningsChanged = hasEvacZones
    ? checkEvacFieldChanged(
        geoEvent.data.hasCustomEvacuationWarnings,
        customWarnings,
        geoEvent.data.evacuationWarnings,
        evacuationWarnings,
      )
    : hasEvacInfoChanged(geoEvent.data.evacuationWarnings, evacuationWarnings);

  const evacuationAdvisoriesChanged = hasEvacZones
    ? checkEvacFieldChanged(
        geoEvent.data.hasCustomEvacuationAdvisories,
        customAdvisories,
        geoEvent.data.evacuationAdvisories,
        evacuationAdvisories,
      )
    : hasEvacInfoChanged(
        geoEvent.data.evacuationAdvisories,
        evacuationAdvisories,
      );

  const evacuationOrdersChanged = hasEvacZones
    ? checkEvacFieldChanged(
        geoEvent.data.hasCustomEvacuationOrders,
        customOrders,
        geoEvent.data.evacuationOrders,
        evacuationOrders,
      )
    : hasEvacInfoChanged(geoEvent.data.evacuationOrders, evacuationOrders);

  const evacuationShelterInPlaceChanged = hasEvacZones
    ? checkEvacFieldChanged(
        geoEvent.data.hasCustomEvacuationShelterInPlace,
        customShelterInPlace,
        geoEvent.data.evacuationShelterInPlace,
        evacuationShelterInPlace,
      )
    : hasEvacInfoChanged(
        geoEvent.data.evacuationShelterInPlace,
        evacuationShelterInPlace,
      );

  const evacZonesRemoved = differenceBy<FormEvacZoneStatus, FormEvacZoneStatus>(
    geoEvent.evacZoneStatuses,
    evacZoneStatuses,
    'evacZone.uidV2',
  );

  const evacZonesAdded = differenceBy<FormEvacZoneStatus, FormEvacZoneStatus>(
    evacZoneStatuses,
    geoEvent.evacZoneStatuses,
    'evacZone.uidV2',
  );

  const { downgradedEvacZones, upgradedEvacZones } = (
    evacZoneStatuses as FormEvacZoneStatus[]
  ).reduce(
    (zones, zone) => {
      if (zone.prevStatus && zone.prevStatus !== zone.status) {
        if (statusIndex[zone.prevStatus] > statusIndex[zone.status]) {
          zones.upgradedEvacZones.push(zone);
        } else {
          zones.downgradedEvacZones.push(zone);
        }
      }
      return zones;
    },
    {
      downgradedEvacZones: [] as FormEvacZoneStatus[],
      upgradedEvacZones: [] as FormEvacZoneStatus[],
    },
  );

  const evacZonesChanged =
    [
      ...evacZonesRemoved,
      ...evacZonesAdded,
      ...downgradedEvacZones,
      ...upgradedEvacZones,
    ].length > 0;

  const evacMessage = useMemo(() => {
    let message = '';
    if (customOrders) {
      message += evacuationOrders;
    }
    if (customWarnings) {
      message += ` ${evacuationWarnings}`;
    }
    if (customAdvisories) {
      message += ` ${evacuationAdvisories}`;
    }
    if (customShelterInPlace) {
      message += ` ${evacuationShelterInPlace}`;
    }
    return message;
  }, [
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationShelterInPlace,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
  ]);

  // Accepted rename holds go by their pending name on the form before submit,
  // so both name matching and messaging must use that name for those zones.
  const { effectiveEvacZones, effectiveZoneNamesByUid } = useMemo(
    () => getEffectiveEvacZones(evacZones, releasePendingZoneUids),
    [evacZones, releasePendingZoneUids],
  );

  const getZoneName = useCallback(
    (zone: { uidV2: string; displayName: string }): string =>
      effectiveZoneNamesByUid.get(zone.uidV2) ?? zone.displayName,
    [effectiveZoneNamesByUid],
  );

  const selectedEvacZoneNames = useMemo(
    () =>
      (evacZoneStatuses as FormEvacZoneStatus[]).reduce((zoneNames, zone) => {
        if (customOrders && zone.status === 'orders') {
          zoneNames.push(getZoneName(zone.evacZone));
        }
        if (customWarnings && zone.status === 'warnings') {
          zoneNames.push(getZoneName(zone.evacZone));
        }
        if (customAdvisories && zone.status === 'advisories') {
          zoneNames.push(getZoneName(zone.evacZone));
        }
        if (customShelterInPlace && zone.status === 'shelter_in_place') {
          zoneNames.push(getZoneName(zone.evacZone));
        }
        return zoneNames;
      }, [] as string[]),
    [
      customAdvisories,
      customOrders,
      customWarnings,
      customShelterInPlace,
      evacZoneStatuses,
      getZoneName,
    ],
  );

  const evacZonesInMessage = useMemo(
    () => findZoneNamesInMessage(effectiveEvacZones, evacMessage),
    [effectiveEvacZones, evacMessage],
  );

  const selectedNotReferencedZoneNames = selectedEvacZoneNames.filter(
    (zoneName) => !evacZonesInMessage.includes(zoneName),
  );

  const referencedNotSelectedZoneNames = evacZonesInMessage.filter(
    (zoneName) => !selectedEvacZoneNames.includes(zoneName),
  );

  const evacTextChanges: Partial<Record<EvacTextChangeKey, string>> = {};
  if (evacuationOrdersChanged) {
    evacTextChanges.evacuationOrders = t(
      EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle].orders,
    );
  }
  if (evacuationWarningsChanged) {
    evacTextChanges.evacuationWarnings = t(
      EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle].warnings,
    );
  }
  if (evacuationAdvisoriesChanged) {
    evacTextChanges.evacuationAdvisories = t(
      EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle].advisories,
    );
  }
  if (evacuationShelterInPlaceChanged) {
    evacTextChanges.evacuationShelterInPlace = t(
      EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle].shelter_in_place,
    );
  }
  if (evacuationNotesChanged) {
    evacTextChanges.evacuationNotes = t('evacZones.notes');
  }

  const zoneMismatchLines: EvacTransLine[] = [
    ...selectedNotReferencedZoneNames.map((zoneName) => ({
      id: `selected-not-referenced-${zoneName}`,
      i18nKey: 'geoEventChangelog.selectedNotReferenced',
      values: { zoneName },
    })),
    ...referencedNotSelectedZoneNames.map((zoneName) => ({
      id: `referenced-not-selected-${zoneName}`,
      i18nKey: 'geoEventChangelog.referencedNotSelected',
      values: { zoneName },
    })),
  ];

  const zoneAddedSection: EvacTransSection | null =
    evacZonesAdded.length > 0
      ? {
          id: 'zone-added',
          title: t('geoEventChangelog.zoneAdded.title'),
          lines: evacZonesAdded.map((zone) => ({
            id: zone.evacZone.uidV2,
            i18nKey: 'geoEventChangelog.zoneAdded.description',
            values: {
              zoneName: getZoneName(zone.evacZone),
              zoneStatus: t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][zone.status]),
            },
          })),
        }
      : null;

  const zoneUpgradedSection: EvacTransSection | null =
    upgradedEvacZones.length > 0
      ? {
          id: 'zone-upgraded',
          title: t('geoEventChangelog.zoneUpgraded.title'),
          lines: upgradedEvacZones.map((zone) => ({
            id: zone.evacZone.uidV2,
            i18nKey: 'geoEventChangelog.zoneUpgraded.description',
            values: {
              zoneName: getZoneName(zone.evacZone),
              prevZoneStatus: t(
                EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][zone.prevStatus!],
              ),
              zoneStatus: t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][zone.status]),
            },
          })),
        }
      : null;

  const zoneDowngradedSection: EvacTransSection | null =
    downgradedEvacZones.length > 0
      ? {
          id: 'zone-downgraded',
          title: t('geoEventChangelog.zoneDowngraded.title'),
          lines: downgradedEvacZones.map((zone) => ({
            id: zone.evacZone.uidV2,
            i18nKey: 'geoEventChangelog.zoneDowngraded.description',
            values: {
              zoneName: getZoneName(zone.evacZone),
              prevZoneStatus: t(
                EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][zone.prevStatus!],
              ),
              zoneStatus: t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][zone.status]),
            },
          })),
        }
      : null;

  const zoneRemovedSection: EvacZoneRemovedSection | null =
    evacZonesRemoved.length > 0
      ? {
          title: t('geoEventChangelog.zoneRemoved'),
          zones: evacZonesRemoved.map((zone) => ({
            uid: zone.evacZone.uidV2,
            displayName: getZoneName(zone.evacZone),
          })),
        }
      : null;

  const hasChanges =
    evacuationNotesChanged ||
    evacuationWarningsChanged ||
    evacuationAdvisoriesChanged ||
    evacuationOrdersChanged ||
    evacuationShelterInPlaceChanged ||
    evacZonesChanged ||
    selectedNotReferencedZoneNames.length > 0 ||
    referencedNotSelectedZoneNames.length > 0;

  return {
    evacTextChanges,
    evacuationInfo: {
      show: evacZonesChanged,
      appendSilentLine: notificationType === NotificationType.Silent,
    },
    zoneMismatchLines,
    zoneAddedSection,
    zoneUpgradedSection,
    zoneDowngradedSection,
    zoneRemovedSection,
    hasChanges,
    showConfirmSelectedZones:
      !isProvisional && selectedNotReferencedZoneNames.length > 0,
  };
};
